import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { NumberMark } from "@/components/ui/NumberMark";
import { cn } from "@/lib/utils";
import type {
  FileResponse,
  InventionElementResponse,
} from "@patent-studio/api-types";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { isAwaitingAttention, tagOf } from "./element-tags";
import { elementCardId } from "./anchors";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { OpenDocument, citedPages } from "../documents/OpenDocument";
import { CardFoot } from "./ElementCardFoot";
import { OpenQuestionOnly } from "./FindingMarks";
import type { LadderWrites } from "./ElementLadder";
import {
  MarkedWords,
  TermField,
  TermNotes,
  drawnMarks,
  useTerm,
} from "./terms";
import {
  AttachesTo,
  RelationField,
  relationDraftOf,
  relationMark,
  relationUnmarked,
  relationWrite,
  type RelationDraft,
} from "./relations";

const CHIP =
  "inline-flex items-center whitespace-nowrap rounded-[5px] border px-[7px] py-[2px] text-[9.5px] font-bold uppercase tracking-[0.03em]";

/** One source as the line names it, and the file behind it where the source names one. */
export interface CitationLine {
  label: string;
  fileId: string | null;
}

/** The task's files by id: whether a file id may be opened, and whether its citations name pages. */
export type Openable = Pick<
  ReadonlyMap<string, Pick<FileResponse, "cites_pages">>,
  "has" | "get"
>;

/** Between one citation and the next on the line. */
export const CITATION_JOIN = " · ";

/**
 * The line in parts: what leads it, and each source in the element's order.
 * The card draws the parts, and the wording lives here alone.
 */
export function provenanceParts(
  element: InventionElementResponse,
  files: Openable,
): {
  lead: string;
  citations: CitationLine[];
} {
  // An inference is never presented as a direct citation: the passages it
  // names are what it was read across, not where it is stated.
  const lead = element.source_tag === "ai_framing" ? "Inferred from " : "";
  const citations = element.sources.map((source) => {
    // Absent rather than empty on a source nobody has located yet: the
    // generated type makes the list optional, and a missing list is not a
    // located source with no pages.
    // Each page named once. An element can rest on two passages of one page,
    // which is two locations and one place to look, so listing both reads
    // "(p.1, p.1)" and says nothing the first one did not.
    const pages = citedPages(
      source.locations,
      source.file_id == null ? undefined : files.get(source.file_id),
    );
    const where = pages.length > 0 ? ` (${pages.join(", ")})` : "";
    return {
      label: `${source.document_label}${where}`,
      fileId: source.file_id ?? null,
    };
  });
  return { lead, citations };
}

/**
 * The attorney stood behind an element that asked for it, by its tag or by a
 * flag's reason, as v0.28 marks "Confirmed by you": a grounded element stood
 * without them, so a yes on it says nothing the card does not.
 */
function confirmedOnAsking(element: InventionElementResponse): boolean {
  return (
    element.confirmed_at !== null &&
    (tagOf(element.source_tag).requiresConfirmation ||
      element.flag_reason !== null)
  );
}

/**
 * The provenance line on the card, one citation per source: each the way
 * into its document at the cited passage where that document is on the task,
 * text where it is not, so a citation of a file since removed is never
 * offered as a link that opens nothing.
 */
function Provenance({
  element,
  openable,
  onOpen,
}: {
  element: InventionElementResponse;
  openable: Openable;
  onOpen: (fileId: string) => void;
}) {
  const { lead, citations } = provenanceParts(element, openable);
  if (citations.length === 0) return null;

  // The attorney's decision is recorded alongside the original citation,
  // never instead of it, so where it came from stays readable after they
  // have stood behind it.
  const confirmed = confirmedOnAsking(element) ? "Confirmed by you · " : "";
  return (
    <p
      data-testid={`element-source-${element.element_id}`}
      className={cn(
        "flex min-w-0 items-start gap-1.5 font-mono text-[10.5px] leading-normal",
        tagOf(element.source_tag).lineClass,
      )}
    >
      <FileText aria-hidden className="mt-px size-3 flex-none" />
      <span className="min-w-0">
        {confirmed}
        {lead}
        {citations.map(({ label, fileId }, i) => (
          <span key={`${String(i)}:${label}`}>
            {i > 0 && CITATION_JOIN}
            {fileId !== null && openable.has(fileId) ? (
              <OpenDocument
                onOpen={() => {
                  onOpen(fileId);
                }}
              >
                {label}
              </OpenDocument>
            ) : (
              label
            )}
          </span>
        ))}
      </span>
    </p>
  );
}

export interface ElementCardProps {
  element: InventionElementResponse;
  busy: boolean;
  /** Whether this element is the one whose editor is open, if any is. */
  amending: boolean;
  /** Another element's editor is open, so this one's Amend waits. */
  amendLocked: boolean;
  /** The last element on the invention: removing it empties the set. */
  only: boolean;
  onAmendOpen: () => void;
  onAmendClose: () => void;
  onConfirm: () => void;
  /** Resolves when the wording is stored. A rejection leaves the editor open. */
  onAmend: (text: string) => Promise<unknown>;
  /** Resolves when the term is stored. Naming moves nothing the attorney confirmed. */
  onName: (term: string) => Promise<unknown>;
  /** Resolves when the relation is stored: the element attached to by words, or to nothing. */
  onRelate: (
    relation: { targetId: string; words: string } | null,
  ) => Promise<unknown>;
  /** The whole set, so the card can name what it attaches to and offer the others. */
  elements: readonly InventionElementResponse[];
  /** Resolves when the element is removed; rejects when the store refuses it. */
  onRemove: () => Promise<unknown>;
  /** The files on the task a citation may open; a citation of any other file stays text. */
  openable: Openable;
  /** Opens the cited document at what the element cites in it. */
  onOpenSource: (fileId: string) => void;
  /** The writes open on the element's ladder. */
  ladder: LadderWrites;
}

/**
 * The inline editor over the wording, the term and the relation. Save is
 * unavailable while the wording says nothing, while an element is chosen to
 * attach to with no words that say how, or while a write is in flight.
 */
function AmendEditor({
  element,
  elements,
  busy,
  onSave,
  onCancel,
}: {
  element: InventionElementResponse;
  elements: readonly InventionElementResponse[];
  busy: boolean;
  /** The wording, the term marked or null where none was, and the relation as drafted. */
  onSave: (text: string, term: string | null, relation: RelationDraft) => void;
  onCancel: () => void;
}) {
  const initial = element.text;
  const [draft, setDraft] = useState(initial);
  const [term, setTerm] = useState("");
  const [relation, setRelation] = useState(() => relationDraftOf(element));
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  useUnsavedDraft(draft !== initial);

  return (
    <div className="flex-1">
      <Textarea
        size="dense"
        ref={fieldRef}
        aria-label="Element wording"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        rows={2}
        className="text-detail"
      />
      <TermField label="Element term" text={draft} onChange={setTerm} />
      <RelationField
        element={element}
        elements={elements}
        text={draft}
        draft={relation}
        onChange={setRelation}
      />
      <div className="mt-2 flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="dense"
          disabled={
            busy || draft.trim() === "" || relationUnmarked(element, relation)
          }
          onClick={() => {
            onSave(draft.trim(), term === "" ? null : term, relation);
          }}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="dense"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The element's place in the sequence.
 *
 * Drawn empty rather than omitted while the element awaits attention, so it is
 * visible that it has not taken its place yet.
 */
function NumberSlot({
  element,
  awaiting,
}: {
  element: InventionElementResponse;
  awaiting: boolean;
}) {
  return (
    <NumberMark
      testId={`element-number-${element.element_id}`}
      number={awaiting ? null : (element.number ?? null)}
      label={
        awaiting ? "No number yet" : `Element ${String(element.number ?? 0)}`
      }
    />
  );
}

/** The badge above the wording: what the element asks, or that it asked and was answered. */
function StateBadge({ element }: { element: InventionElementResponse }) {
  // The design sets this in the card's top-right corner rather than above the
  // wording, which is why the wording row keeps a gutter clear for it.
  const pill =
    "absolute right-[11px] top-[9px] rounded-full border px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.06em] text-white";
  if (isAwaitingAttention(element)) {
    return (
      <span className={cn(pill, "border-sidebar bg-sidebar")}>
        Requires your attention
      </span>
    );
  }
  if (confirmedOnAsking(element)) {
    return (
      <span className={cn(pill, "border-success bg-success")}>
        Confirmed by you
      </span>
    );
  }
  return null;
}

/** The element's number and its words, or the editor open over them. */
function Wording({
  element,
  elements,
  awaiting,
  amending,
  busy,
  onAmend,
  onName,
  onRelate,
  onAmendClose,
}: Pick<
  ElementCardProps,
  | "element"
  | "elements"
  | "amending"
  | "busy"
  | "onAmend"
  | "onName"
  | "onRelate"
  | "onAmendClose"
> & { awaiting: boolean }) {
  const term = useTerm(element.element_id);
  const link = relationMark(element, elements);
  const marks = link === null ? [] : [link];
  const linked = link !== null && drawnMarks(term, marks).includes(link);
  return (
    <div className="flex items-start gap-[9px] pr-[150px]">
      <NumberSlot element={element} awaiting={awaiting} />
      {amending ? (
        <AmendEditor
          element={element}
          elements={elements}
          busy={busy}
          onSave={(text, marked, relation) => {
            // Closed only once all are stored, for the reason the add form is:
            // the editor holds the only copy of the rewrite. The wording goes
            // first, so a term and a relation are read against the words they
            // will stand in, and nothing unchanged is written, which would
            // lapse the confirmation for a change that is not there.
            const reworded =
              text === element.text ? Promise.resolve() : onAmend(text);
            const write = relationWrite(element, relation);
            void reworded
              .then(() => (marked === null ? undefined : onName(marked)))
              .then(() =>
                write === null
                  ? undefined
                  : onRelate("clear" in write ? null : write),
              )
              .then(onAmendClose, () => undefined);
          }}
          onCancel={onAmendClose}
        />
      ) : (
        <div className="flex-1">
          <p className="text-[16px] font-medium leading-[1.45]">
            &ldquo;
            <MarkedWords text={element.text} term={term} marks={marks} />
            &rdquo;
          </p>
          <TermNotes term={term} />
          <AttachesTo element={element} elements={elements} linked={linked} />
        </div>
      )}
    </div>
  );
}

/**
 * One element of the invention: its place in the sequence, its wording, where it
 * came from, and what the attorney may do with it.
 *
 * The number comes from the server. An element awaiting attention has none, and
 * the slot is drawn empty rather than omitted, so it is visible that the element
 * has not taken its place in the sequence yet.
 */
export function ElementCard(props: ElementCardProps) {
  const { element, openable, onOpenSource } = props;
  const tag = tagOf(element.source_tag);
  const awaiting = isAwaitingAttention(element);

  return (
    <Panel
      as="li"
      pad="snug"
      id={elementCardId(element.element_id)}
      data-testid={elementCardId(element.element_id)}
      // Focusable by script only, so the footer's link lands here for a keyboard
      // reader too, without the card joining the tab order.
      tabIndex={-1}
      className={cn(
        "relative border-l-[3px]",
        tag.cardClass,
        // A settled element carries a quiet green ring, whatever its source
        // colour. The ring says it is done; the left edge still says where it
        // came from, which confirmation never changes.
        !awaiting && "ring-1 ring-success/45",
      )}
    >
      <StateBadge element={element} />

      <Wording {...props} awaiting={awaiting} />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className={cn(CHIP, tag.chipClass)}>{tag.label}</span>
        {element.sources.some((source) => source.removed) && (
          <StatusPill tone="warning" size="sm">
            Source removed, re-confirm
          </StatusPill>
        )}
        <OpenQuestionOnly
          id={element.element_id}
          uncertainOnly={element.uncertain_only}
          keptReasons={element.kept_reasons}
        />
        {/* Beside the tag, as the design reads a source: the tag, then where. */}
        <Provenance
          element={element}
          openable={openable}
          onOpen={onOpenSource}
        />
      </div>

      <CardFoot {...props} awaiting={awaiting} />
    </Panel>
  );
}
